import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ImagePlus, AlertCircle, RotateCcw, Upload, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { useApi } from '../../../../hooks/useApi';
import { WB_W, WB_H, WhiteboardShapes } from './whiteboardShapes';
import { InterpretationReview, ModePicker } from './InterpretationReview';
import { PromptBox, PROMPT_LIMIT } from './PromptBox';

const MAX_SIDE = 1600;

// Downscale in the browser (smaller upload, faster AI); the server re-encodes again and strips metadata.
function readPhoto(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.88));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('This photo format is not supported here. Please use a JPG, PNG or WebP image.')); };
    img.src = url;
  });
}

function PhotoPicker({ photo, disabled, onPick }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-3">
      <button type="button" disabled={disabled} onClick={() => ref.current?.click()} data-testid="wb-photo-pick"
        className="h-24 w-36 flex-shrink-0 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 overflow-hidden flex items-center justify-center text-xs text-slate-500">
        {photo ? <img src={photo} alt="Your whiteboard photo (private)" className="h-full w-full object-cover" data-testid="wb-photo-thumb" />
          : <span className="flex flex-col items-center gap-1"><Upload className="h-5 w-5" />Upload photo</span>}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/*" className="hidden" data-testid="wb-photo-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} />
      <p className="text-xs text-muted-foreground">
        Take or upload a photo of a whiteboard, sketch or hand-drawn diagram. The photo stays private: it is only used for this
        reconstruction, is never saved to your deck and is never shown on shared links.
      </p>
    </div>
  );
}

const LAYOUT_NAMES = { 'photo-iceberg': 'Iceberg layout', 'photo-faithful': 'Faithful Reconstruction', photo: 'Professional Redesign' };

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div data-testid="wb-photo-error" className="flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm">
      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
    </div>
  );
}

function Preview({ result, background, hasObjects }) {
  return (
    <div className="space-y-2" data-testid="wb-photo-preview">
      <div className="rounded-lg border border-slate-200 overflow-hidden" style={{ backgroundColor: background || '#FFFFFF' }}>
        <svg viewBox={`0 0 ${WB_W} ${WB_H}`} className="w-full h-auto block" aria-label="Reconstruction preview">
          <WhiteboardShapes objects={result.objects} />
        </svg>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="wb-photo-summary">
        Picture to Perfect · <span data-testid="wb-photo-layout">{LAYOUT_NAMES[result.layout] || 'Reconstruction'}</span> · {result.objects.length} editable objects · AI confidence: <b data-testid="wb-photo-confidence">{result.confidence}</b>
        {result.unresolved > 0 ? ` · ${result.unresolved} unresolved item(s): marked ? on the board or listed in the notes below` : ''}.
        {hasObjects ? ' Accepting will replace the objects currently on this whiteboard.' : ''}
      </p>
      {result.needs_review && (
        <p data-testid="wb-photo-review" className="text-xs font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-3 py-2">
          Review before using: check the notes below and compare with your photo. You can fix anything on the board after accepting.
        </p>
      )}
      {result.warnings?.length > 0 && (
        <ul data-testid="wb-photo-warnings" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-0.5">
          {result.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}
    </div>
  );
}

// Photo -> ONE AI read -> user reviews/corrects the interpretation -> deterministic preview (free, any mode) -> Accept.
// The board is untouched until accepted; the photo and interpretation are never saved.
export function PictureToPerfectDialog({ open, onOpenChange, hasObjects, background, onAccept }) {
  const { id: deckId } = useParams();
  const { post } = useApi();
  const [photo, setPhoto] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | review | building | preview | error
  const [interp, setInterp] = useState(null);
  const [mode, setMode] = useState('redesign');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const busy = status === 'loading' || status === 'building';

  const reset = () => { setPhoto(null); setStatus('idle'); setResult(null); setInterp(null); setMode('redesign'); setError(''); setInstructions(''); };
  const close = () => { if (busy) return; onOpenChange(false); reset(); };
  const pick = async (file) => {
    setError('');
    try { setPhoto(await readPhoto(file)); setStatus('idle'); setResult(null); setInterp(null); } catch (e) { setError(e.message); setStatus('error'); }
  };
  const read = async () => {
    if (!photo || busy || instructions.length > PROMPT_LIMIT) return;
    if (interp && !window.confirm('Read the photo again with AI? This uses AI credits and discards your corrections.')) return;
    const back = interp ? 'review' : 'error';
    setStatus('loading'); setError('');
    try {
      const res = await post('/whiteboard-ai/reconstruct', { deck_id: deckId, image_base64: photo, instructions: instructions.trim(), mode });
      setInterp(res.interpretation); setResult(null); setStatus('review');
    } catch (e) { setError(e.message || 'AI could not read the photo. Nothing was changed.'); setStatus(back); }
  };
  const build = async (m = mode) => {
    if (busy) return;
    const back = status;
    setMode(m); setStatus('building'); setError('');
    try {
      setResult(await post('/whiteboard-ai/reconstruct/apply', { deck_id: deckId, interpretation: interp, mode: m }));
      setStatus('preview');
    } catch (e) { setError(e.message || 'Could not build the preview. Nothing was changed.'); setStatus(back); }
  };
  const accept = () => {
    if (hasObjects && !window.confirm('Replace everything currently on this whiteboard with the reconstruction?')) return;
    onAccept(result.objects); onOpenChange(false); reset();
  };
  const reviewing = interp && !result && (status === 'review' || status === 'building' || status === 'loading');
  const previewing = result && (status === 'preview' || status === 'building');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] rounded-lg sm:max-w-[780px] max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&>*]:min-w-0" data-testid="wb-photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ImagePlus className="h-4 w-4" />Picture to Perfect</DialogTitle>
          <DialogDescription data-testid="wb-photo-step">
            {reviewing ? 'Step 2 of 3 · Check what the AI read. Correct anything, then build the preview (no extra AI credits).'
              : previewing ? 'Step 3 of 3 · Preview. Switch style or go back to the review for free.'
                : 'AI reads your photo, you check its reading, then it is rebuilt as editable shapes, lines and labels.'}
          </DialogDescription>
        </DialogHeader>
        {!reviewing && !previewing && (
          <div className="space-y-3">
            <PhotoPicker photo={photo} disabled={busy} onPick={pick} />
            <PromptBox testid="wb-photo-instructions" value={instructions} disabled={busy} onChange={setInstructions} minRows={3}
              placeholder="Optional, as detailed as you like, e.g. The pink line is a progression toward War/Separation, not a strike-through. The wavy turquoise line is a waterline." />
            <p className="text-xs text-muted-foreground" data-testid="wb-photo-credit-note">
              Reading the photo uses AI once and may consume credits; it counts toward your daily AI limit. Your corrections and style changes afterwards are free. Nothing on your board changes until you accept.
            </p>
            {status === 'error' && <ErrorNote error={error} />}
          </div>
        )}
        {reviewing && (
          <div className="space-y-4">
            <InterpretationReview value={interp} onChange={setInterp} />
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Reconstruction style</h4>
              <ModePicker mode={mode} onChange={setMode} disabled={busy} />
            </div>
            <ErrorNote error={error} />
          </div>
        )}
        {previewing && (
          <div className="space-y-3">
            <ModePicker mode={mode} onChange={(m) => m !== mode && build(m)} disabled={busy} />
            <Preview result={result} background={background} hasObjects={hasObjects} />
            <ErrorNote error={error} />
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={close} disabled={busy} data-testid="wb-photo-cancel">Cancel</Button>
          {previewing && (
            <>
              <Button variant="outline" onClick={() => { setStatus('review'); setResult(null); }} disabled={busy} data-testid="wb-photo-back-to-review"><ArrowLeft className="h-4 w-4 mr-1" />Back to review</Button>
              <Button onClick={accept} disabled={busy} data-testid="wb-photo-accept">{hasObjects ? 'Replace whiteboard' : 'Add to whiteboard'}</Button>
            </>
          )}
          {reviewing && (
            <>
              <Button variant="outline" onClick={read} disabled={busy} data-testid="wb-photo-retry">
                {status === 'loading' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}Re-read photo
              </Button>
              <Button onClick={() => build()} disabled={busy || !interp.nodes.length} data-testid="wb-photo-build">
                {status === 'building' ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Building…</> : 'Build preview'}
              </Button>
            </>
          )}
          {!reviewing && !previewing && (
            <Button onClick={read} disabled={!photo || busy || instructions.length > PROMPT_LIMIT} data-testid="wb-photo-reconstruct">
              {status === 'loading' ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Reading photo…</> : 'Read photo with AI'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
